import React from 'react';
import i18n from 'i18next';

import { useTranslation } from 'react-i18next';

import WidgetEvent from '../../../../models/widgetEvent';
import Event from '../../../../models/eventModel';

import useAppContext from '../../../../context/useAppContext';

import { InfoText } from '@weezevent/nacre';
import { SelectedWidgets, EventStatus } from '../selected-widgets/selected-widgets';
import { ListSelect } from '../inputs/inputs';

import Style from './widget-selector.module.css';

export const useDebounce = (func, time = 500) => {
    const timeout = React.useRef(null);

    const wrapped = React.useCallback(
        (...args) => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }

            timeout.current = setTimeout(() => {
                func(...args);
            }, time);
        },
        [func, time]
    );

    return wrapped;
};

class DefaultWidget {
    constructor(event) {
        this.event = event;
        this.is_event = true;

        this.name = i18n.t('ticket.widgets.event.widget_default_name', {
            event: this.event
        });

        return new Proxy(this, {
            get: (obj, prop) => {
                if (Object.hasOwnProperty.call(obj, prop)) {
                    return obj[prop];
                }

                return obj.event[prop];
            }
        });
    }
}

function WidgetItem({ widget }) {
    const { t } = useTranslation();

    const formatter = new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short'
    });
    const event_start_date = formatter.format(new Date(widget.event.start_date));

    return (
        <div className={Style['widget-item']}>
            <InfoText
                subtitle={t('ticket.widgets.event.widget_selector_subtitle', {
                    event_date: event_start_date,
                    event_id: `E${widget.event.id}`,
                    widget_name: widget.name
                })}
            >
                {widget.event.name}
            </InfoText>
            <div className={Style['widget-item-status']}>
                <EventStatus event={widget.event} />
            </div>
        </div>
    );
}

export function WidgetEventsSelector({ events = [], widget_events, onChange, disabled = false }) {
    // This component performs some magic:
    // 1/ It is able to "fake" default widgets, thanks to the DefaultWidget class (up there)
    // 2/ Once it selects/deselects something, it is able to put it in the right place
    // 3/ it performs a "search" on both events and widget-events

    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const query = {
        publication_status__in: 'draft,published',
        sync: 'False'
    };

    const [eventModels, loadingEvents, syncEvents] = Event.useApiModel({
        organizationId: organization.pk,
        query: query
    });

    const [widgetEventModels, , syncWidgetEvents] = WidgetEvent.useApiModel({
        organizationId: organization.pk,
        query: {
            sync: false,
            expand: 'event'
        }
    });

    // Map on all evnts to get list of "default" widgets
    // + map on all widgets
    const selected_widgets = [...widget_events, ...(events?.map(ev => new DefaultWidget(ev)) || [])];
    const selected_ids = selected_widgets.map(w => w.id);

    // Same as on selected
    const selectable_widgets = [...(widgetEventModels || []), ...(eventModels?.map(event => new DefaultWidget(event)) || [])].sort((a, b) => {
        return a.event.name < b.event.name ? -1 : a.event.name === b.event.name ? 0 : 1;
    });

    const _search = React.useCallback(
        value => {
            syncEvents({
                query: {
                    ...query,
                    search: value
                }
            });

            syncWidgetEvents({
                query: {
                    sync: false,
                    expand: 'event',
                    search: value
                }
            });
        },
        [syncEvents, syncWidgetEvents, query]
    );

    const search = useDebounce(_search);

    return (
        <div className={Style['widget-selector-container']}>
            <ListSelect
                search
                disabled={disabled}
                useNew
                value={null}
                loading={loadingEvents}
                closeOnListClick={false}
                label={
                    <div className="widget-selector-label">
                        <div>{t('ticket.widgets.event.config.events_label')}</div>
                        <div className="widget-selector-label-count">
                            {t('ticket.widgets.event.config.events_label_count_interval', {
                                postProcess: 'interval',
                                count: selected_ids?.length || 0
                            })}
                        </div>
                    </div>
                }
                placeholder={t('ticket.widgets.event.config.events_placeholder')}
                options={selectable_widgets
                    ?.filter(({ id }) => !selected_ids.includes(id))
                    .map(widget => ({
                        text: <WidgetItem widget={widget} />,
                        label: widget.name,
                        value: widget.id,
                        object: widget
                    }))}
                onChange={(value, { object }) => {
                    if (object.is_event) {
                        // Add to list of events
                        onChange('events', [...events, object.event]);
                        return;
                    }

                    // Add to list of widgets
                    onChange('widget_events', [...widget_events, object]);
                }}
                onInputChange={({ target: { value } }) => search(value)}
            >
                <SelectedWidgets
                    widgets={selected_widgets}
                    onRemoveWidget={widget => {
                        if (widget.is_event) {
                            const events_by_pk = events.reduce((acc, event) => {
                                acc[event.pk] = event;
                                return acc;
                            }, {});

                            // default widget, remove event
                            delete events_by_pk[widget.event.id];
                            return onChange('events', Object.values(events_by_pk));
                        }

                        // remove selected widget
                        const widget_by_pk = widget_events.reduce((acc, widget) => {
                            acc[widget.pk] = widget;
                            return acc;
                        }, {});

                        delete widget_by_pk[widget.id];
                        onChange('widget_events', Object.values(widget_by_pk));
                    }}
                />
            </ListSelect>
        </div>
    );
}
